/**
 * Shared filter vocabulary for the two hardware list pages (system + organization).
 *
 * These lived as copy-pasted constants in both files, which is how the two views
 * drifted apart on chip handling in the first place. The normalisers in
 * particular must not diverge: they are what keeps a URL shared by an older build
 * from crashing the page.
 */

import {
  ACTIVE,
  LOST,
  MAINTENANCE,
  MISSING,
  REBALANCE,
  RETIRED,
  STORED,
} from '../../constants';

/** Labelled "State" because that is the name of the hardware field. */
export const stateOptions = [
  { value: ACTIVE, label: 'Active' },
  { value: MAINTENANCE, label: 'Maintenance' },
  { value: RETIRED, label: 'Retired' },
  { value: REBALANCE, label: 'Rebalancing' },
  { value: MISSING, label: 'Missing' },
  { value: LOST, label: 'Lost' },
  { value: STORED, label: 'Stored' },
];

/** Tri-state filters: '' = All (no filter), 'true' = Yes, 'false' = No. */
export const yesNoOptions = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

/** How many hardware types get a quick-access toggle under the search bar. */
export const QUICK_TYPE_LIMIT = 5;

/**
 * Whether a tri-state filter value is actually set. Tolerates undefined, which is
 * what a URL written before the filter existed restores as.
 */
export const isYesNo = (value: string | undefined): boolean =>
  value === 'true' || value === 'false';

/**
 * `useUrlState` replaces the whole state object with whatever a shared `?state=`
 * URL decodes to — it does not merge over the initial state. The organization view
 * used to persist `status` and `types` as comma-joined STRINGS (its old
 * handleChipsChange did `values.join(',')`), so a URL written by an earlier build
 * restores them as strings where an array is now expected. Without normalising,
 * `.forEach` on a restored string throws during render and white-screens the page.
 *
 * Applied to both views so the two cannot drift, and so a future shape change is
 * absorbed in one place.
 */
export const toStates = (value: unknown): number[] => {
  const raw = Array.isArray(value) ? value : String(value ?? '').split(',');
  return raw.map(Number).filter((item) => !Number.isNaN(item) && item !== 0);
};

export const toTypeNames = (value: unknown): string[] => {
  const raw = Array.isArray(value) ? value : String(value ?? '').split(',');
  return (
    raw
      .map((item) => String(item).trim())
      // 'all' was the old placeholder value for "no type filter".
      .filter((item) => item !== '' && item !== 'all')
  );
};
